// AddThemeDialog.tsx
// 新規テーマ追加ダイアログ

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Grid,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ThemeSettingPanel from '../../ThemeManagement/Components/ThemeSettingPanel';
import ThemePreviewFull from './ThemePreviewFull';
import { createTheme } from '../../../service/ThemeTypeService';
import { ThemeType } from '../../ThemeManagement/data/ThemeType';

interface AddThemeDialogProps {
  open: boolean;
  typeName: string;
  onAdd: () => void; // 成功時に親コンポーネントにデータ再取得を通知
  onCancel: () => void;
}

// バリデーションエラーのインターフェース
interface ValidationErrors {
  themeName?: string;
}

  // 新規テーマの初期値（ThemeType形式）
// デフォルトテーマ設定
const DEFAULT_THEME: ThemeType = {
  id: '',
  themeName: '',
  iconName: 'AssignmentTwoToneIcon',
  iconColor: '#1976d2',
  iconBgColor: '#e3f2fd',
  iconBdColor: '#90caf9',
  buttonColor: '#e3f2fd',
  fontColor: '#1565c0',
  buttonBdColor: '#90caf9',
  themeDescription: '',
};

/**
 * 新規テーマ追加ダイアログコンポーネント
 * - テーマ名・アイコン・ボタン色などの設定と即時プレビューを提供する
 * - 保存時にAPIを呼び出し、成功後に親コンポーネントへ通知する
 * - props: open (ダイアログ表示状態), typeName (申請種別名), onAdd (追加成功コールバック),
 *          onCancel (キャンセルコールバック)
 */
export default function AddThemeDialog({ open, typeName, onAdd, onCancel }: AddThemeDialogProps) {
  // 新規テーマの初期値（ThemeType形式）
  const [newTheme, setNewTheme] = useState<ThemeType>(DEFAULT_THEME);

  // バリデーションエラー
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({});

  // テーマ変更ハンドラー
  const handleThemeChange = (updatedTheme: Omit<ThemeType, 'id'>) => {
    setNewTheme({ ...newTheme, ...updatedTheme });
    // テーマ名が変更された場合、エラーをクリア
    if (updatedTheme.themeName && validationErrors.themeName && updatedTheme.themeName.trim()) {
      setValidationErrors({ ...validationErrors, themeName: undefined });
    }
  };

  // 保存処理
  const handleAdd = async () => {
    // バリデーション
    const errors: ValidationErrors = {};

    if (!newTheme.themeName.trim()) {
      errors.themeName = '※テーマ名を入力してください';
    }

    // エラーがある場合は保存しない
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    // API 呼び出し
    const success = await createTheme({
      themeName: newTheme.themeName,
      iconName: newTheme.iconName,
      iconColor: newTheme.iconColor,
      iconBgColor: newTheme.iconBgColor,
      iconBdColor: newTheme.iconBdColor,
      buttonColor: newTheme.buttonColor,
      fontColor: newTheme.fontColor,
      buttonBdColor: newTheme.buttonBdColor,
      themeDescription: newTheme.themeDescription,
    });

    if (success) {
      // 成功したら親コンポーネントに通知してデータを再取得させる
      onAdd();

      // フォームリセット
      setNewTheme(DEFAULT_THEME);
      setValidationErrors({});
    } else {
      alert('テーマの作成に失敗しました');
    }
  };

  // キャンセル処理
  const handleCancel = () => {
    // フォームリセット
    setNewTheme(DEFAULT_THEME);
    setValidationErrors({});
    onCancel();
  };

  return (
    <Dialog open={open} onClose={handleCancel} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body1" fontWeight="bold" sx={{ color: 'GrayText' }}>
            新規テーマ追加
          </Typography>
          <EditIcon sx={{ fontSize: 20, color: 'GrayText' }} />
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={3}>
          {/* 左側: テーマ設定 */}
          <Grid size={{ xs: 12, md: 7 }}>
            <ThemeSettingPanel theme={newTheme} onChange={handleThemeChange} validationErrors={validationErrors} />
          </Grid>

          {/* 右側: プレビュー */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ position: 'sticky', top: 20 }}>
              <ThemePreviewFull
                typeName={typeName}
                theme={{
                  themeId: newTheme.id,
                  themeName: newTheme.themeName,
                  iconName: newTheme.iconName,
                  iconColor: newTheme.iconColor,
                  iconBgColor: newTheme.iconBgColor,
                  iconBdColor: newTheme.iconBdColor,
                  buttonColor: newTheme.buttonColor,
                  fontColor: newTheme.fontColor,
                  buttonBdColor: newTheme.buttonBdColor,
                  themeDescription: newTheme.themeDescription,
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} color="inherit">
          キャンセル
        </Button>
        <Button onClick={handleAdd} variant="contained" color="primary">
          追加
        </Button>
      </DialogActions>
    </Dialog>
  );
}